import { useAvailablePromoCodesList } from '@if/core'
import { isAfter } from '@if/core/date'
import { EFinancingProgram, EPaymentPlan, EProvince } from '@if/core/types'
import { formatUtcIsoDateTime } from '@if/core/utils'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import { useFinancingConfig } from '@src/data/selectors/app-config-selectors'
import { Constants } from '@src/data/types'
import { ETermMax } from '@src/data/types/constants'
import { CreditApplication } from '@src/data/types/CreditApplicationSchema'
import { NormCreditDecision } from '@src/data/types/CreditDecisionSchema'
import { FinancingConfigDto } from '@src/data/types/FinancingConfigDto'
import { Merchant } from '@src/data/types/Merchant'
import { MerchantPaymentPlan } from '@src/data/types/MerchantPaymentPlan'
import { useMemo } from 'react'
import { BaseWorksheetFormConfig } from './BaseWorksheetForm.config'

const computeAvailablePaymentPlans = (
  plans: MerchantPaymentPlan[],
  availablePromoCodes: Set<string>,
  createdOn: Date,
): MerchantPaymentPlan[] => {
  const now = new Date()
  const nowUtc = new Date(formatUtcIsoDateTime(now))
  const creationDate = createdOn ? new Date(createdOn) : null
  const DISTANT_FUTURE = new Date('2099-12-31T23:59:59.999Z')
  const isWithin = (date: Date, start: Date, end: Date) => end > start && date >= start && date <= end

  return plans.filter((plan) => {
    if (plan.id === EPaymentPlan.RegularDailyInterests) return true

    const activeFrom = new Date(plan.activeFrom)
    const expiresOn = new Date(plan.expiresOn ?? DISTANT_FUTURE)
    const promoCode = plan.promoCode.split(' ')[0]

    const isDateValid =
      ((creationDate && isWithin(creationDate, activeFrom, expiresOn)) || isWithin(now, activeFrom, expiresOn)) &&
      isAfter(expiresOn, nowUtc)
    return isDateValid && availablePromoCodes.has(promoCode)
  })
}

type ProgramConfig = (typeof Constants.financingProgramConfigs)[keyof typeof Constants.financingProgramConfigs]

const resolveProgramConfig = (financingProgramId: EFinancingProgram): ProgramConfig =>
  Constants.financingProgramConfigs[financingProgramId] ?? Constants.financingProgramConfigs.default

const mergeLoanAmount = (financingValue: number, programFallback: number | undefined): number => {
  if (financingValue > 0) return financingValue
  if (programFallback && programFallback > 0) return programFallback
  return 0
}

export interface BuildBaseWorksheetFormConfigInput {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  merchantId: string
  financingConfig: FinancingConfigDto
  stateIso: EProvince
  hasCoApplicant: boolean
  interestRate: number
  maxPmtAmount?: number
  createdOn: Date
  expiresOn: Date | string
  merchant?: Merchant | null
  normsCreditDecision?: NormCreditDecision | null
  maxAmountFinanced?: number | null
  maxTermDuration?: number | null
  editLocked?: boolean
  isSinglePayment?: boolean
  availablePromoCodes?: Set<string>
  // Overrides the program default when terms are restricted by decision context (e.g. CreditRsla plan).
  supportedTermsOverride?: ETermMax[]
}

export const buildBaseWorksheetFormConfig = (input: BuildBaseWorksheetFormConfigInput): BaseWorksheetFormConfig => {
  const {
    financingProgramId,
    creditApplicationId,
    merchantId,
    financingConfig,
    stateIso,
    hasCoApplicant,
    interestRate,
    maxPmtAmount,
    createdOn,
    expiresOn,
    merchant,
    normsCreditDecision,
    maxAmountFinanced,
    maxTermDuration,
    editLocked,
    isSinglePayment,
    availablePromoCodes,
    supportedTermsOverride,
  } = input

  const programConfig = resolveProgramConfig(financingProgramId)
  const plans = merchant?.paymentPlans ?? []
  const availablePaymentPlans = computeAvailablePaymentPlans(
    plans,
    availablePromoCodes ?? new Set<string>(),
    createdOn,
  )

  return {
    financingProgramId,
    creditApplicationId,
    merchantId,
    minLoanAmount: mergeLoanAmount(financingConfig.minimumLoanAmount, programConfig.minLoanAmount),
    maxLoanAmount: mergeLoanAmount(financingConfig.maximumLoanAmount, programConfig.maxLoanAmount),
    minLoanTerm: financingConfig.minLoanTerm,
    maxLoanTerm: financingConfig.maxLoanTerm,
    largeLoanAmount: financingConfig.largeLoanAmount,
    longTerm: financingConfig.longTerm,
    effectiveRateThreshold: financingConfig.effectiveRateThreshold,
    supportedTerms: supportedTermsOverride ?? programConfig.supportedTerms,
    supportedPaymentFrequencies: programConfig.supportedPaymentFrequencies,
    supportsInsurance: programConfig.supportsInsurance,
    supportsHighEstimate: programConfig.supportsHighEstimate,
    useAgentLeeway: programConfig.useAgentLeeway,
    validateMaxPayment: programConfig.validateMaxPaymentAmountInWorksheet,
    filteredTermsByMaxPayment: programConfig.filteredTermsByMaxPayment,
    checkAvailableCredit: programConfig.checkAvailableCredit,
    checkActivationWithin90Days: programConfig.checkActivationWithin90Days,
    normsCreditDecision: normsCreditDecision ?? null,
    maxAmountFinanced,
    maxTermDuration,
    canAcceptHighEstimate: merchant?.canAcceptHighEstimate,
    hasCoApplicant,
    stateIso,
    interestRate,
    maxPmtAmount,
    plans,
    availablePaymentPlans,
    planPromoRules: merchant?.planPromoRules,
    createdOn,
    expiresOn,
    editLocked,
    isSinglePayment,
  }
}

export interface ResolvedFinancingConfig {
  financingConfig: FinancingConfigDto
  isLoading: boolean
}

export const useResolvedFinancingConfig = (
  financingProgramId: EFinancingProgram | undefined,
  merchantId: string | undefined,
): ResolvedFinancingConfig => {
  const programFinancingConfig = useFinancingConfig(financingProgramId)
  const [merchantFinancingConfig, isFetchingMerchantConfig] = useMerchantFinancingConfig(
    merchantId,
    financingProgramId,
  )
  const isLoading = !!merchantId && !!financingProgramId && !merchantFinancingConfig && isFetchingMerchantConfig
  return {
    financingConfig: merchantFinancingConfig ?? programFinancingConfig,
    isLoading,
  }
}

export interface UseBaseWorksheetFormConfigFromCreditAppInput {
  creditApplication: CreditApplication
  merchant: Merchant | undefined | null
}

export interface BaseWorksheetFormConfigResult {
  config: BaseWorksheetFormConfig
  isLoading: boolean
}

export const useBaseWorksheetFormConfigFromCreditApp = ({
  creditApplication,
  merchant,
}: UseBaseWorksheetFormConfigFromCreditAppInput): BaseWorksheetFormConfigResult => {
  const { financingConfig, isLoading } = useResolvedFinancingConfig(
    creditApplication.financingProgramId,
    merchant?.id,
  )
  const programConfig = resolveProgramConfig(creditApplication.financingProgramId)

  const decision = creditApplication.finalCreditDecision
  const interestRate = decision?.interestRate ?? programConfig.minInterestRate

  const availablePromoCodes = useAvailablePromoCodesList(
    merchant?.planPromoRules,
    creditApplication.normsCreditDecision,
    creditApplication.financingProgramId,
  )

  const config = useMemo(
    () =>
      buildBaseWorksheetFormConfig({
        financingProgramId: creditApplication.financingProgramId,
        creditApplicationId: creditApplication.id,
        merchantId: merchant?.id ?? '',
        financingConfig,
        stateIso: creditApplication.applicant.currentAddress.stateIso,
        hasCoApplicant: !!creditApplication.coApplicant,
        interestRate,
        maxPmtAmount: decision?.maxPmtAmount ?? 0,
        createdOn: creditApplication.createdOn,
        expiresOn: creditApplication.expiresOn,
        merchant: merchant ?? null,
        normsCreditDecision: creditApplication.normsCreditDecision,
        maxAmountFinanced: decision?.maxAmountFinanced ?? null,
        maxTermDuration: decision?.maxTermDuration ?? null,
        editLocked: creditApplication.editLocked,
        availablePromoCodes,
      }),
    [creditApplication, merchant, financingConfig, interestRate, decision, availablePromoCodes],
  )

  return { config, isLoading }
}
